import {
  type CompressionConfig,
  type RenditionSpec,
  getRenditionSpecs,
} from "@homethyst/shared/photo";

export type { CompressionConfig };

export interface WebRenditions {
  displayBlob: Blob;
  thumbBlob: Blob;
  originalBlob?: Blob;
}

async function resizeToBlob(file: File | Blob, spec: RenditionSpec): Promise<Blob> {
  const bitmap = await createImageBitmap(file instanceof File ? file : file);
  const { width, height } = bitmap;
  const scale = Math.min(1, spec.maxEdge / Math.max(width, height));
  const w = Math.round(width * scale);
  const h = Math.round(height * scale);

  const canvas = new OffscreenCanvas(w, h);
  const ctx = canvas.getContext("2d")!;
  ctx.drawImage(bitmap, 0, 0, w, h);
  bitmap.close();

  return canvas.convertToBlob({ type: "image/jpeg", quality: spec.quality });
}

/**
 * Produce tier-appropriate renditions from a File.
 */
export async function compressImage(file: File, config?: CompressionConfig): Promise<WebRenditions> {
  const specs = getRenditionSpecs(config);

  if (specs.original) {
    const [originalBlob, displayBlob, thumbBlob] = await Promise.all([
      resizeToBlob(file, specs.original),
      resizeToBlob(file, specs.display),
      resizeToBlob(file, specs.thumb),
    ]);
    return { originalBlob, displayBlob, thumbBlob };
  }

  const [displayBlob, thumbBlob] = await Promise.all([
    resizeToBlob(file, specs.display),
    resizeToBlob(file, specs.thumb),
  ]);
  return { displayBlob, thumbBlob };
}
